import { CalendarIcon, UfoIcon } from '@/assets/icons'
import Spinner from '@components/Spinner'
import { usePublicItinerary } from '@hooks'
import { fromUtcDate, toDashed } from '@lib/datetime'
import { TripHelper } from '@lib/TripHelper'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
	EmptyState,
	EmptyStateContainer,
	EmptyStateIcon,
	EmptyStateText,
	ItineraryPageContainer,
} from '../Itinerary/ItineraryPage.styles'
import { Itinerary } from '../Itinerary/types'
import { generateItinerary } from '../Itinerary/utils'
import {
	CenteredContainer,
	ErrorMessage,
	ErrorTitle,
	LoadingText,
	SpinnerWrapper,
} from './PublicItineraryPage.styles'
import ReadOnlyBoard from './ReadOnlyBoard'

const PublicItineraryPage: React.FC = () => {
	const { tripCode } = useParams<{ tripCode: string }>()
	const { data, isLoading, error } = usePublicItinerary(tripCode)
	const [itinerary, setItinerary] = useState<Itinerary | null>(null)

	const trip = data?.trip
	const tripStartDate = trip?.startDate ? TripHelper.convertTripDateToInputFormat(trip.startDate) : ''
	const tripEndDate = trip
		? (() => {
				if (!trip.startDate || !trip.dayCount) return ''
				const tripHelper = new TripHelper(trip)
				const endDate = tripHelper.getEndDate()
				return endDate ? toDashed(fromUtcDate(endDate)) : ''
			})()
		: ''

	useEffect(() => {
		if (trip) {
			setItinerary(generateItinerary(trip, data?.events, data?.tripDayInfo))
		}
	}, [trip, data?.events, data?.tripDayInfo])

	if (isLoading) {
		return (
			<CenteredContainer>
				<SpinnerWrapper>
					<Spinner />
				</SpinnerWrapper>
				<LoadingText>Loading itinerary…</LoadingText>
			</CenteredContainer>
		)
	}

	if (error || !trip) {
		return (
			<CenteredContainer>
				<UfoIcon width={250} height={250} />
				<ErrorTitle>Itinerary Unavailable</ErrorTitle>
				<ErrorMessage>
					This itinerary doesn't exist or has been set to private by the trip owner.
				</ErrorMessage>
			</CenteredContainer>
		)
	}

	return (
		<ItineraryPageContainer>
			{!itinerary ? (
				<EmptyStateContainer>
					<EmptyState>
						<EmptyStateIcon>
							<CalendarIcon width={128} height={128} fill="currentColor" />
						</EmptyStateIcon>
						<EmptyStateText>This trip does not have itinerary dates yet.</EmptyStateText>
					</EmptyState>
				</EmptyStateContainer>
			) : (
				<ReadOnlyBoard
					itinerary={itinerary}
					tripName={trip.name}
					dayCount={trip.dayCount}
					tripStartDate={tripStartDate}
					tripEndDate={tripEndDate}
					travelSegments={data?.travelSegments}
				/>
			)}
		</ItineraryPageContainer>
	)
}

export default PublicItineraryPage
